// @ts-nocheck
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import QRScanner from './QRScanner';
import ShimmerButton from './ShimmerButton';
import AutoProgressButton from './AutoProgressButton';
import { api } from '../services/api';
import {
    analytics,
    trackPagePerformance,
    trackErrorWithContext,
    getSessionContext,
} from '../utils/analytics';
import { AnalyticsEvents } from '../utils/analytics';
import { useAnalyticsOnce } from '../hooks/useAnalyticsOnce';

interface PairWithScreenProps {
    onQRScan: (deviceId: string, deviceCode: string) => void;
    onSaveForLater: () => void;
    queueData: any;
    userId: string;
    isAutoPaired?: boolean;
    onContinue?: () => void;
    isPairing?: boolean;
}

export default function PairWithScreen({
    onQRScan,
    onSaveForLater,
    queueData,
    userId,
    isAutoPaired = false,
    onContinue,
    isPairing = false,
}: PairWithScreenProps) {
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showManualEntry, setShowManualEntry] = useState(false);
    const [manualDeviceId, setManualDeviceId] = useState('');
    const [manualDeviceCode, setManualDeviceCode] = useState('');
    const { captureOnce } = useAnalyticsOnce();

    // Check if we arrived here due to pairing failure or from already paired modal
    const showPairingFailureMessage =
        queueData?.pairingFailed && !queueData?.fromAlreadyPaired;
    const showAlreadyPairedMessage = queueData?.fromAlreadyPaired;

    // Track (non-blocking) component mounting only once using captureOnce
    useEffect(() => {
        // Track (non-blocking) pairing screen shown (not a step completion, just a view event)
        captureOnce(
            'pair_screen_viewed',
            {
                view_type: 'initial_load',
                user_id: userId,
                has_queue_data: !!queueData,
                has_channel_id: !!queueData?.channelId,
                artworks_count: queueData?.artworks?.length || 0,
                pairing_failed: !!showPairingFailureMessage,
                already_paired: !!showAlreadyPairedMessage,
                error_message: queueData?.errorMessage || null,
                ...getSessionContext(),
            },
            'pair_screen_viewed_session' // Unique key for this session
        );

        // Track (non-blocking) performance
        trackPagePerformance();
    }, []); // Empty dependency array - only runs once on mount

    // Track (non-blocking) error states in a separate effect
    useEffect(() => {
        // Only track if we have an actual error state
        if (showPairingFailureMessage || showAlreadyPairedMessage) {
            analytics.capture(AnalyticsEvents.PAIRING_FAILED, {
                failure_type: showPairingFailureMessage
                    ? 'pairing_failed'
                    : 'already_paired',
                user_id: userId,
                error_message:
                    queueData?.errorMessage || 'Device pairing issue',
                channel_id: queueData?.channelId || null,
                ...getSessionContext(),
            });
        }
    }, [showPairingFailureMessage, showAlreadyPairedMessage]);

    const handleSaveForLater = async () => {
        const startTime = Date.now();

        // Track save for later button click immediately
        analytics.capture('save_for_later_clicked', {
            user_id: userId,
            channel_id: queueData?.channelId || null,
            channel_name: queueData?.channelName || null,
            artworks_count: queueData?.artworks?.length || 0,
            step: 'pairing_screen',
            ...getSessionContext(),
        });

        setIsSaving(true);
        setError(null);

        try {
            console.log('[PAIR-APP] Preparing queue for save...', queueData);
            console.log('[PAIR-APP] Queue artworks:', queueData?.artworks);
            console.log('[PAIR-APP] Queue channelId:', queueData?.channelId);

            // Check artwork structure
            if (queueData?.artworks?.length > 0) {
                console.log(
                    '[PAIR-APP] First artwork structure:',
                    queueData.artworks[0]
                );
                console.log(
                    '[PAIR-APP] Has artworkID?',
                    'artworkID' in queueData.artworks[0]
                );
                console.log(
                    '[PAIR-APP] Has id?',
                    'id' in queueData.artworks[0]
                );
            }

            // Build queue in the same format as setDeviceQueue
            // Handle both fresh channels (with 'id') and saved queues (with 'artworkID')
            const artworksForQueue = (queueData?.artworks || []).map(
                (artwork: any) => ({
                    artworkID: artwork.artworkID || artwork.id, // Handle both formats
                    originalChannelID: queueData.channelId,
                    downloaded: false,
                })
            );

            console.log(
                '[PAIR-APP] Mapped artworks for queue:',
                artworksForQueue
            );

            const queuePlaylist = [
                {
                    section: 'channel',
                    channelID: queueData?.channelId,
                    artworks: artworksForQueue,
                },
            ];

            console.log(
                '[PAIR-APP] Final queuePlaylist to save:',
                JSON.stringify(queuePlaylist)
            );
            console.log('[PAIR-APP] Saving queue for later:', queuePlaylist);
            const result = await api.saveQueueForLater(queuePlaylist);

            if (result.success) {
                console.log('[PAIR-APP] Queue saved successfully');
                setShowSuccessModal(true);

                // Track (non-blocking) successful save
                analytics.capture(AnalyticsEvents.ARTCAST_SAVED_FOR_LATER, {
                    user_id: userId,
                    channel_id: queueData?.channelId || null,
                    channel_name: queueData?.channelName || null,
                    artworks_count: queueData?.artworks?.length || 0,
                    has_channel_name: !!queueData?.channelName,
                    step: 'save_successful',
                    pairing_status: 'not_paired_saved_for_later',
                    processing_time: Date.now() - startTime,
                    ...getSessionContext(),
                });

                // Track (non-blocking) that pairing was skipped in favor of save for later
                analytics.capture(AnalyticsEvents.PAIRING_SKIPPED, {
                    reason: 'saved_for_later',
                    user_id: userId,
                    channel_id: queueData?.channelId || null,
                    ...getSessionContext(),
                });
            } else {
                console.error('[PAIR-APP] Failed to save queue:', result.error);
                setError(result.error || 'Failed to save art');

                // Track (non-blocking) save failure
                analytics.capture(AnalyticsEvents.ERROR_OCCURRED, {
                    error_type: 'save_queue_failed',
                    error_message: result.error || 'Failed to save art',
                    user_id: userId,
                    channel_id: queueData?.channelId || null,
                    processing_time: Date.now() - startTime,
                    ...getSessionContext(),
                });
            }
        } catch (err) {
            console.error('[PAIR-APP] Error saving queue:', err);
            setError('An error occurred while saving');

            // Track (non-blocking) save exception
            await trackErrorWithContext(
                err instanceof Error ? err : new Error(String(err)),
                {
                    step: 'save_for_later',
                    user_id: userId,
                    channel_id: queueData?.channelId || null,
                    error_type: 'save_exception',
                }
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleModalClose = () => {
        setShowSuccessModal(false);
        onSaveForLater();
        // Removed redundant tracking - already tracked in handleSaveArtcastForLater
    };

    const handleManualPair = async () => {
        if (manualDeviceId.trim() && manualDeviceCode.trim()) {
            console.log('[PAIR-APP] Manual pairing with:', {
                deviceId: manualDeviceId,
                deviceCode: manualDeviceCode,
            });

            // Track (non-blocking) manual pairing attempt
            analytics.capture(AnalyticsEvents.DEVICE_CODE_ENTERED, {
                user_id: userId,
                method: 'manual_entry',
                device_id: manualDeviceId.trim(),
                has_device_code: !!manualDeviceCode.trim(),
                channel_id: queueData?.channelId || null,
                ...getSessionContext(),
            });

            // Use the same onQRScan callback with manual values
            onQRScan(manualDeviceId.trim(), manualDeviceCode.trim());
            setShowManualEntry(false);
            setManualDeviceId('');
            setManualDeviceCode('');
        } else {
            // Track (non-blocking) incomplete manual entry
            analytics.capture(AnalyticsEvents.PAIRING_FAILED, {
                failure_type: 'incomplete_manual_entry',
                user_id: userId,
                has_device_id: !!manualDeviceId.trim(),
                has_device_code: !!manualDeviceCode.trim(),
                error_message: 'Incomplete manual device entry',
                ...getSessionContext(),
            });
        }
    };

    const handleShowManualEntry = async () => {
        setShowManualEntry(true);

        // Track (non-blocking) manual entry opened - single event
        analytics.capture(AnalyticsEvents.MANUAL_ENTRY_OPENED, {
            step: 'manual_entry_shown',
            trigger: 'user_clicked_button',
            user_id: userId,
            channel_id: queueData?.channelId || null,
            ...getSessionContext(),
        });
    };

    const handleHideManualEntry = async () => {
        setShowManualEntry(false);

        // Track (non-blocking) manual entry cancelled (not a step completion)
        analytics.capture('manual_entry_cancelled', {
            action: 'user_cancelled',
            user_id: userId,
            channel_id: queueData?.channelId || null,
            ...getSessionContext(),
        });
    };

    return (
        <motion.div
            className="w-full max-w-md mx-auto px-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
        >
            {/* Show auto-pairing success UI when isAutoPaired is true */}
            {isAutoPaired && !showPairingFailureMessage ? (
                <div className="space-y-6">
                    {/* Auto-pairing success notification */}
                    <motion.div
                        className="bg-green-900/30 rounded-xl p-6 border border-green-500/30"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        <p className="text-green-300 font-medium text-center text-lg mb-2">
                            🎉 Your screen was paired automatically!
                        </p>
                        <p className="text-green-200 text-center">
                            Your selected art is being sent to your display.
                        </p>
                    </motion.div>

                    {/* Auto-progress Continue button */}
                    <div className="text-center">
                        <AutoProgressButton
                            onClick={onContinue}
                            onAutoProgress={onContinue}
                            autoProgressDelay={3000}
                            className="w-full max-w-xs"
                        >
                            Continue
                        </AutoProgressButton>
                    </div>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Default Welcome Message - show when no special conditions */}
                    {!showPairingFailureMessage &&
                        !showAlreadyPairedMessage && (
                            <motion.div
                                className="text-center p-4 bg-gradient-to-r from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.3 }}
                            >
                                <p className="text-white text-base mb-2">
                                    🎨 Almost there!
                                </p>
                                <p className="text-gray-300 text-sm">
                                    Scan the QR code on your screen to start
                                    enjoying your artcast, or save it for later
                                    if you're not near a screen right now.
                                </p>
                            </motion.div>
                        )}

                    {/* Pairing Failure Message */}
                    {showPairingFailureMessage && (
                        <motion.div
                            className="text-center p-4 bg-yellow-900/20 border border-yellow-700/50 rounded-lg"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            <p className="text-yellow-400 text-sm mb-2">
                                ⚠️ Device pairing failed
                            </p>
                            <p className="text-gray-400 text-sm">
                                The device couldn't be paired. Please scan the
                                QR code on your screen to try again.
                            </p>
                        </motion.div>
                    )}

                    {/* Already Paired Message */}
                    {showAlreadyPairedMessage && (
                        <motion.div
                            className="text-center p-4 bg-purple-900/20 border border-purple-700/50 rounded-lg"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            <p className="text-purple-400 text-sm mb-2">
                                🔄 Ready to pair a new device
                            </p>
                            <p className="text-gray-400 text-sm">
                                Scan the QR code on the screen you want to pair.
                            </p>
                        </motion.div>
                    )}

                    {/* QR Scanner Section */}
                    <QRScanner onScanSuccess={onQRScan} disabled={isPairing} />

                    {/* Manual Entry Link - with minimal spacing */}
                    <div className="text-center">
                        <button
                            type="button"
                            onClick={handleShowManualEntry}
                            disabled={isPairing}
                            className={`text-sm underline transition-colors duration-200 py-1 ${
                                isPairing
                                    ? 'text-gray-600 cursor-not-allowed'
                                    : 'text-gray-400 hover:text-gray-300'
                            }`}
                        >
                            Device ID and Code
                        </button>
                    </div>

                    {/* OR Divider - with minimal spacing */}
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-700"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-black text-gray-400">
                                OR
                            </span>
                        </div>
                    </div>

                    {/* Save for Later Button */}
                    <div className="text-center">
                        <p className="text-gray-400 mb-4">
                            Not in front of a screen?
                        </p>
                        <ShimmerButton
                            onClick={handleSaveForLater}
                            disabled={isSaving}
                            className="w-full max-w-xs"
                        >
                            {isSaving ? (
                                <span className="flex items-center justify-center gap-2">
                                    <svg
                                        className="animate-spin h-5 w-5"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                            fill="none"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                        />
                                    </svg>
                                    Saving...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center gap-2">
                                    <svg
                                        className="h-5 w-5"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    Save Art for Later
                                </span>
                            )}
                        </ShimmerButton>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <motion.div
                            className="text-center p-4 bg-red-900/20 border border-red-700/50 rounded-lg"
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.3 }}
                        >
                            <p className="text-red-400 text-sm">❌ {error}</p>
                        </motion.div>
                    )}
                </div>
            )}

            {/* Success Modal */}
            <AnimatePresence>
                {showSuccessModal && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div
                            className="absolute inset-0 bg-black/80"
                            onClick={handleModalClose}
                        />
                        <motion.div
                            className="relative bg-gray-900 rounded-2xl p-8 max-w-sm w-full"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ type: 'spring', duration: 0.5 }}
                        >
                            <div className="text-center">
                                <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg
                                        className="w-8 h-8 text-green-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M5 13l4 4L19 7"
                                        />
                                    </svg>
                                </div>
                                <h3 className="text-xl font-bold text-white mb-2">
                                    Art Saved!
                                </h3>
                                <p className="text-gray-400 mb-6">
                                    Your selected art has been saved and is
                                    waiting for you. When you're in front of a
                                    screen, come back to pair your device and
                                    enjoy your art.
                                </p>
                                <ShimmerButton
                                    onClick={handleModalClose}
                                    className="w-full"
                                >
                                    Continue
                                </ShimmerButton>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Manual Entry Modal */}
            <AnimatePresence>
                {showManualEntry && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                    >
                        <div
                            className="absolute inset-0 bg-black/80"
                            onClick={handleHideManualEntry}
                        />
                        <motion.div
                            className="relative bg-gray-900 rounded-2xl p-8 max-w-sm w-full"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            transition={{ type: 'spring', duration: 0.5 }}
                        >
                            {/* Close button */}
                            <button
                                onClick={handleHideManualEntry}
                                className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
                            >
                                <svg
                                    className="w-6 h-6"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </button>

                            <div className="text-center mb-6">
                                <h3 className="text-xl font-bold text-white mb-2">
                                    Enter Device Details
                                </h3>
                                <p className="text-gray-400 text-sm">
                                    Manually enter your device ID and code to
                                    pair
                                </p>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label
                                        htmlFor="deviceId"
                                        className="block text-sm font-medium text-gray-300 mb-2 text-center"
                                    >
                                        Device ID
                                    </label>
                                    <input
                                        id="deviceId"
                                        type="text"
                                        value={manualDeviceId}
                                        onChange={(e) =>
                                            setManualDeviceId(
                                                e.target.value.toUpperCase()
                                            )
                                        }
                                        placeholder="XXXXXX"
                                        className="w-full px-4 py-4 bg-gray-800 text-white rounded-lg border border-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-center text-2xl font-mono tracking-wider"
                                        style={{
                                            fontFamily:
                                                "'Courier New', Courier, monospace",
                                        }}
                                        autoFocus
                                    />
                                </div>

                                <div>
                                    <label
                                        htmlFor="deviceCode"
                                        className="block text-sm font-medium text-gray-300 mb-2 text-center"
                                    >
                                        Device Code
                                    </label>
                                    <input
                                        id="deviceCode"
                                        type="text"
                                        value={manualDeviceCode}
                                        onChange={(e) =>
                                            setManualDeviceCode(
                                                e.target.value.toUpperCase()
                                            )
                                        }
                                        placeholder="XXXX"
                                        className="w-full px-4 py-4 bg-gray-800 text-white rounded-lg border border-gray-700 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-500/50 text-center text-2xl font-mono tracking-wider"
                                        style={{
                                            fontFamily:
                                                "'Courier New', Courier, monospace",
                                        }}
                                    />
                                </div>

                                <ShimmerButton
                                    onClick={handleManualPair}
                                    disabled={
                                        !manualDeviceId.trim() ||
                                        !manualDeviceCode.trim()
                                    }
                                    className="w-full mt-6"
                                >
                                    Pair Device
                                </ShimmerButton>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}
